import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Smartphone, X } from 'lucide-react';
import { formatCode, remoteUrl, obsUrl, isLocalHost, normalizeBaseUrl } from './remoteProtocol';

const BASE_KEY = 'sleekHearRemoteBase';
const readBase = () => { try { return localStorage.getItem(BASE_KEY) || ''; } catch { return ''; } };
const saveBase = (v) => { try { localStorage.setItem(BASE_KEY, v); } catch { /* not remembered */ } };

/**
 * Pairing dialog for the phone remote: shows a QR code and the code to type,
 * whether a phone is connected, and ends or renews the session.
 */
const RemotePanel = ({ remote, onClose, dm, t }) => {
  const { code, active, status, phoneConnected, start, end, regenerate } = remote;
  const [qr, setQr] = useState('');
  const [copied, setCopied] = useState(false);
  // On localhost the phone needs this computer's network address instead.
  const onLocalhost = isLocalHost(window.location.hostname);
  const [typedBase, setTypedBase] = useState(readBase);
  const base = onLocalhost ? normalizeBaseUrl(typedBase) : window.location.origin;
  const link = active && base ? remoteUrl(base, code) : '';

  useEffect(() => {
    let cancelled = false;
    if (!link) { setQr(''); return undefined; }
    QRCode.toDataURL(link, { margin: 1, width: 240 })
      .then(url => { if (!cancelled) setQr(url); })
      .catch(() => { if (!cancelled) setQr(''); });
    return () => { cancelled = true; };
  }, [link]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-label="Phone remote">
      <div className={`w-full max-w-sm max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl border p-5 shadow-2xl ${t.cardBg} ${dm ? 'bg-slate-800' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-3">
          <h2 className={`flex items-center gap-2 text-lg font-bold ${t.textPrimary}`}>
            <Smartphone className="w-5 h-5" /> Phone remote
          </h2>
          <button onClick={onClose} aria-label="Close" className={`p-1 rounded ${t.dropdownItem}`}><X className="w-5 h-5" /></button>
        </div>

        {!active ? (
          <>
            <p className={`text-sm mb-4 ${t.textSecondary}`}>
              Let a volunteer move the run-sheet and send slides live from their phone. No password is shared.
            </p>
            <button onClick={start} className="w-full px-4 py-3 bg-brand-600 text-slate-50 rounded-lg font-bold hover:bg-brand-500">
              Start remote session
            </button>
          </>
        ) : (
          <>
            <p className={`text-sm mb-3 ${t.textSecondary}`}>Scan with the phone camera, or open the link and type the code.</p>
            {onLocalhost && (
              <div className="mb-3">
                <label className={`block text-xs font-bold mb-1 ${t.textPrimary}`} htmlFor="remote-base">
                  This computer's network address (the phone can't open "localhost")
                </label>
                <input
                  id="remote-base"
                  value={typedBase}
                  onChange={(e) => { setTypedBase(e.target.value); saveBase(e.target.value); }}
                  placeholder="e.g. 192.168.1.57:3000"
                  inputMode="url"
                  autoComplete="off"
                  className={`w-full p-2 text-sm rounded-lg border ${t.inputBg || ''}`}
                />
                <p className={`text-xs mt-1 ${t.textMuted}`}>
                  Find it with <span className="font-mono">ipconfig</span> (IPv4 Address) or in the terminal line "On Your Network". The phone must be on the same Wi-Fi. If the computer lists several addresses (Wi-Fi and Ethernet), try each one in the phone's browser; use the one that opens.
                </p>
              </div>
            )}
            <div className="flex justify-center mb-3">
              {link && qr
                ? <img src={qr} alt="QR code to open the remote" className="w-48 h-48 rounded-lg bg-white p-1" />
                : <div className="w-48 h-48 rounded-lg bg-slate-700/40" />}
            </div>
            <div className={`text-center text-3xl font-mono font-bold tracking-widest mb-1 ${t.textPrimary}`} aria-label="Pairing code">
              {formatCode(code)}
            </div>
            <p className={`text-center text-xs mb-3 break-all ${t.textMuted}`}>{link ? link.replace(/^https?:\/\//, '').replace(/\?c=.*/, '') : 'Enter the address above'}</p>

            <div className={`flex items-center gap-2 p-2 rounded-lg border mb-4 ${t.innerCardBg}`}>
              <span className={`w-2.5 h-2.5 rounded-full ${phoneConnected ? 'bg-green-400' : status === 'connected' ? 'bg-yellow-400' : 'bg-slate-400'}`} />
              <span className={`text-sm ${t.textPrimary}`}>
                {phoneConnected ? 'Phone connected'
                  : status === 'connected' ? 'Waiting for a phone…'
                  : status === 'connecting' ? 'Connecting…'
                  : 'Offline — check your internet connection'}
              </span>
            </div>

            <div className="flex gap-2">
              <button onClick={regenerate} className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border ${t.innerCardBg} ${t.textPrimary}`}>
                New code
              </button>
              <button onClick={() => { end(); onClose(); }} className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-500">
                End session
              </button>
            </div>
            <div className={`mt-4 pt-3 border-t ${t.divider || ''}`}>
              <label className={`block text-xs font-bold mb-1 ${t.textPrimary}`} htmlFor="obs-link">Livestream (OBS Browser Source)</label>
              <div className="flex gap-2">
                <input
                  id="obs-link"
                  readOnly
                  value={obsUrl(window.location.origin, code)}
                  onFocus={(e) => e.target.select()}
                  className={`flex-1 min-w-0 p-2 text-xs font-mono rounded-lg border ${t.inputBg || ''}`}
                />
                <button
                  onClick={() => {
                    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
                    if (navigator.clipboard) navigator.clipboard.writeText(obsUrl(window.location.origin, code)).then(done, () => {});
                  }}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border ${t.innerCardBg} ${t.textPrimary}`}
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className={`text-xs mt-1 ${t.textMuted}`}>
                In OBS: Sources → + → Browser, paste this link, set the size to 1920 × 1080. Verses and lyrics appear as a lower third while you are LIVE. It follows the same code, so ending the session also stops it.
              </p>
            </div>
            <p className={`text-xs mt-3 ${t.textMuted}`}>A new code or ending the session disconnects the phone straight away.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default RemotePanel;
